import { AlertTriangle, Copy, Settings } from 'lucide-react';
import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';
import {
  type ConfigProfileFormDefaultValues,
  type ITechnology,
  useConfigurationProfileForm,
} from '../../../../hooks/production/use-config-profile-form';
import {
  useInactivateConfigurationProfile,
  useValidateConfigurationProfile,
} from '../../../../hooks/production/use-configuration-profiles';
import { useToast } from '../../../../hooks/use-toast';
import { configurationProfileConstants } from '../../../../lib/constants/configuration-profile';
import type { ConfigProfileFilterStatus } from '../../../../types/production/configuration-profile-list.type';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../ui/alert-dialog';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../ui/form';
import { Input } from '../../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { GeneralConfigurationProfileForm } from './general-config-form';
import { SpecificBwsLlNbConfigurationProfileForm } from './specific-bws-ll-nb-form';
import { SpecificBws4GConfigurationProfileForm } from './specific-bws4g-form';
import { SpecificBws4GLoraConfigurationProfileForm } from './specific-bws4g-lora-form';
import { SpecificE3PlusConfigurationProfileForm } from './specific-e3plus-form';
import { SpecificE3Plus4GConfigurationProfileForm } from './specific-e3plus4g-form';
import { SpecificLoRaConfigurationProfileForm } from './specific-lora-form';
import { SpecificNB2ConfigurationProfileForm } from './specific-nb2-form';
import { SpecificNB2LoraConfigurationProfileForm } from './specific-nb2lora-form';

interface Props {
  technologies: ITechnology[];
  defaultValues?: ConfigProfileFormDefaultValues;
  isEdit?: boolean;
  isCopy?: boolean;
}

const STATUS_CONFIG: Record<
  ConfigProfileFilterStatus,
  { label: string; variant: 'success-subtle' | 'warning-subtle' | 'secondary-subtle' }
> = {
  ATIVO: { label: 'Ativo', variant: 'success-subtle' },
  INATIVO: { label: 'Inativo', variant: 'secondary-subtle' },
  AGUARDANDO_VALIDACAO: {
    label: 'Aguardando Validação',
    variant: 'warning-subtle',
  },
};

export function ConfigurationProfileUpsertForm(props: Props) {
  const { technologies, defaultValues, isEdit, isCopy } = props;
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    form,
    handleChangeName,
    handleSubmit,
    handleChangeTechnology,
    technology,
    status,
    isActive,
    isInactive,
    isAwaitingValidation,
    isLocked,
    isSubmitting,
  } = useConfigurationProfileForm(props);
  const inactivateMutation = useInactivateConfigurationProfile();
  const validateMutation = useValidateConfigurationProfile();
  const [showInactivateConfirmation, setShowInactivateConfirmation] = useState(false);

  // TODO: Reativar quando o campo de cliente voltar a ser obrigatório.
  // A infra de busca/seleção de cliente foi removida junto com o campo comentado
  // no JSX (ver FormField "client_id"). Restaurar handleChangeClient,
  // selectedClients e useConfigurationProfilesClients ao reabilitar.

  const statusKey: ConfigProfileFilterStatus = status ?? 'AGUARDANDO_VALIDACAO';
  const statusConfig = STATUS_CONFIG[statusKey];
  const shouldInactivate = !!isEdit && isActive;
  const shouldValidate = !!isEdit && isAwaitingValidation;
  const shouldCopy = !!isEdit && (isActive || isInactive) && !!defaultValues?.configurationProfile;
  const profileId = defaultValues?.configurationProfile?.id;
  const isActionPending =
    isSubmitting || inactivateMutation.isPending || validateMutation.isPending;

  async function handleInactivateProfile() {
    if (!profileId) return;

    try {
      await inactivateMutation.mutateAsync(profileId);
      setShowInactivateConfirmation(false);
      toast({
        title: 'Perfil inativado',
        description: 'O perfil foi inativado com sucesso.',
        variant: 'success',
      });
      navigate('/production/config-profiles');
    } catch {
      toast({
        title: 'Erro ao inativar perfil',
        description: 'Não foi possível inativar o perfil.',
        variant: 'destructive',
      });
    }
  }

  async function handleValidateProfile() {
    if (!profileId) return;

    try {
      await validateMutation.mutateAsync(profileId);
      toast({
        title: 'Perfil validado',
        description: 'O perfil foi validado com sucesso.',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Erro ao validar perfil',
        description: 'Não foi possível validar o perfil.',
        variant: 'destructive',
      });
    }
  }

  function handleCopyProfile() {
    const sourceProfile = defaultValues?.configurationProfile;
    if (!sourceProfile) return;

    navigate('/production/configuration-profile/form/create', {
      state: { copyFrom: sourceProfile },
    });
  }

  function renderSpecificForm(system: string | undefined) {
    if (isLocked) return null;
    switch (system) {
      case 'DM_E3_PLUS_4G':
        return <SpecificE3Plus4GConfigurationProfileForm />;
      case 'DM_E3_PLUS':
        return <SpecificE3PlusConfigurationProfileForm />;
      case 'DM_BWS_NB2':
        return <SpecificNB2ConfigurationProfileForm />;
      case 'DM_BWS_NB2_LORA':
        return <SpecificNB2LoraConfigurationProfileForm />;
      case 'DM_BWS_LORA':
        return <SpecificLoRaConfigurationProfileForm />;
      case 'DM_BWS_4G':
        return <SpecificBws4GConfigurationProfileForm />;
      case 'DM_BWS_PLUS_4G_LORA':
        return <SpecificBws4GLoraConfigurationProfileForm />;
      case 'DM_BWS_LL_NB':
      case 'DM_BWS_LL_NB_LORA':
      case 'DM_BWS_BAIT_NB':
      case 'DM_BWS_BAIT_NB_LORA':
        return <SpecificBwsLlNbConfigurationProfileForm />;
      default:
        return null;
    }
  }

  return (
    <FormProvider {...form}>
      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Seção Identificação */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Identificação do Perfil
                {isEdit && <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>}
              </CardTitle>
              <CardDescription>
                {isLocked
                  ? 'Este perfil não pode ser editado neste status.'
                  : 'Informações básicas para identificar o perfil de configuração'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="technology_id"
                  defaultValue={defaultValues?.technology?.id}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tecnologia</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          handleChangeTechnology(value);
                        }}
                        defaultValue={field.value}
                        disabled={isLocked}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma tecnologia" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {technologies.map((tech) => (
                            <SelectItem key={tech.id} value={tech.id}>
                              {tech.name.brand}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  defaultValue={isCopy ? undefined : defaultValues?.configurationProfile?.type}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo</FormLabel>
                      <Select
                        onValueChange={(type) => {
                          field.onChange(type);
                          handleChangeName({
                            type,
                          });
                        }}
                        defaultValue={field.value}
                        disabled={isLocked}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um tipo" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(configurationProfileConstants.type).map(
                            ([label, value]) => (
                              <SelectItem key={label} value={label}>
                                {value}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/*
                  TODO: Campo de cliente temporariamente desativado.
                  Futuramente o cliente voltará a ser obrigatório; por enquanto
                  fica comentado (e client_id opcional no schema) para permitir
                  criar perfis sem cliente vinculado.
                <FormField
                  control={form.control}
                  name="client_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cliente</FormLabel>
                      <Combobox
                        data={clients}
                        keyExtractor={(client) => client.id}
                        displayValueGetter={(client) =>
                          client.fantasy_name || client.name || client.document?.value || ''
                        }
                        value={selectedClients}
                        placeholder={
                          clientNotAvailable ? 'Cliente não disponível' : 'Buscar cliente...'
                        }
                        type="single"
                        disableLocalFilter
                        isLoading={isLoadingClients}
                        disabled={isLocked || clientNotAvailable}
                        onSearchChange={(query) => setClientSearch(query)}
                        onOptionChange={(selected) => {
                          setSelectedClients(selected);
                          const client = selected[0] || null;
                          field.onChange(client?.id || '');
                          if (client) {
                            const detail =
                              client.fantasy_name || client.name || client.document?.value || '';
                            handleChangeClient(detail);
                          } else {
                            handleChangeClient('');
                          }
                        }}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                */}
              </div>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Perfil</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={isLocked} placeholder="Digite o nome do perfil" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {isLocked && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="flex items-center gap-3 pt-6">
                <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Edição bloqueada para perfil {statusConfig.label.toLowerCase()}
                  </p>
                  <p className="text-sm text-yellow-700">
                    Use as ações disponíveis para copiar ou alterar o status do perfil.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {!isLocked &&
          technology?.name.system !== 'DM_BWS_LORA' &&
          technology?.name.system !== 'DM_BWS_NB2_LORA' ? (
            <GeneralConfigurationProfileForm technology={technology} />
          ) : null}

          {!isLocked && technology && renderSpecificForm(technology.name.system)}

          {(!isLocked || !!isEdit) && (
            <Card>
              <CardFooter className="pt-6 flex flex-wrap justify-between gap-3">
                <Button variant="outline" type="button" onClick={() => navigate(-1)}>
                  Cancelar
                </Button>
                <div className="flex flex-wrap justify-end gap-2">
                  {shouldCopy && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isActionPending}
                      onClick={handleCopyProfile}
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      Copiar Perfil
                    </Button>
                  )}
                  {shouldValidate && (
                    <Button
                      type="button"
                      disabled={isActionPending}
                      onClick={handleValidateProfile}
                    >
                      {validateMutation.isPending ? 'Validando...' : 'Validar Perfil'}
                    </Button>
                  )}
                  {shouldInactivate && (
                    <Button
                      type="button"
                      disabled={isActionPending}
                      onClick={() => setShowInactivateConfirmation(true)}
                    >
                      {inactivateMutation.isPending ? 'Inativando...' : 'Inativar Perfil'}
                    </Button>
                  )}
                  {!isLocked && (
                    <Button type="submit" disabled={isActionPending}>
                      {isSubmitting ? 'Salvando...' : 'Salvar Perfil'}
                    </Button>
                  )}
                </div>
              </CardFooter>
            </Card>
          )}
        </form>
      </Form>
      <AlertDialog open={showInactivateConfirmation} onOpenChange={setShowInactivateConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar perfil</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá alterar o status do perfil para Inativo. Confirme para continuar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={inactivateMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={inactivateMutation.isPending}
              onClick={handleInactivateProfile}
            >
              {inactivateMutation.isPending ? 'Inativando...' : 'Confirmar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormProvider>
  );
}
